import { classes } from 'html-classes';
import type { ComponentType } from 'react';
import { Fragment } from 'react';

import BadgeCheckIcon from '../icons/BadgeCheckIcon';
import BadgePercentIcon from '../icons/BadgePercentIcon';
import MedalCheckIcon from '../icons/MedalCheckIcon';
import MessageCircleIcon from '../icons/MessageCircleIcon';
import PlaneIcon from '../icons/PlaneIcon';
import ShoppingBagIcon from '../icons/ShoppingBagIcon';
import ShoppingCartIcon from '../icons/ShoppingCartIcon';
import StarIcon from '../icons/StarIcon';

type IconComponent = ComponentType<{ className?: string }>;

interface Advantage {
  icon: IconComponent;
  label: string;
}

interface TrustStat {
  icon: IconComponent;
  value: string;
  label: string;
  /** Shortened label used on mobile, where the design keeps two columns. */
  shortLabel: string;
}

const ADVANTAGES: Advantage[] = [
  { icon: BadgeCheckIcon, label: 'Оригинальные бренды' },
  { icon: PlaneIcon, label: 'Доставка из-за рубежа' },
  { icon: MedalCheckIcon, label: 'Гарантия качества' },
  { icon: BadgePercentIcon, label: 'Выгодные цены' },
];

const STATS: TrustStat[] = [
  {
    icon: StarIcon,
    value: '5 из 5',
    label: 'Оценка товаров',
    shortLabel: 'Оценка',
  },
  {
    icon: ShoppingBagIcon,
    value: '377К',
    label: 'Доставленных заказов',
    shortLabel: 'Заказов',
  },
  {
    icon: MessageCircleIcon,
    value: '145К',
    label: 'Отзывов',
    shortLabel: 'Отзывов',
  },
  {
    icon: ShoppingCartIcon,
    value: 'с 2024 года',
    label: 'Продаем на Озоне',
    shortLabel: 'на Озоне',
  },
];

// Both rows share one column track so their columns and dividers line up: two
// equal columns on mobile, the design's 218/250/172/231 track on desktop.
const rowClassName =
  'grid grid-cols-2 gap-2.5 px-2.5 py-3 min-[1025px]:grid-cols-[218px_1px_250px_1px_172px_1px_231px] min-[1025px]:items-center min-[1025px]:justify-center min-[1025px]:gap-x-8 min-[1025px]:gap-y-0 min-[1025px]:px-5 min-[1025px]:py-4';
// The dubaiexpress theme lacks `--graphic-transparent-black-15`, hence the literal.
const dividerClassName =
  'hidden w-px self-stretch bg-[rgba(0,0,0,0.15)] min-[1025px]:block';
const itemClassName = 'flex items-center gap-1 min-[1025px]:gap-1.5';
const iconClassName = 'size-4 shrink-0 text-icon-brand min-[1025px]:size-5';
const textClassName =
  'm-0 whitespace-nowrap font-customer-text text-[12px] text-text-secondary leading-[14px] tracking-tight min-[1025px]:text-[16px] min-[1025px]:text-text-primary min-[1025px]:leading-[18px]';

/**
 * DubaiExpress home panel: shop advantages on a white card with the trust
 * numbers on a tinted strip underneath.
 */
export const TrustPanel = ({ className }: { className?: string }) => (
  <div
    className={classes(['rounded-[16px] bg-surface-transparent-2', className])}
  >
    <div
      className={classes([
        rowClassName,
        'rounded-[16px] border-2 border-surface-transparent-2 bg-bg-base',
      ])}
    >
      {ADVANTAGES.map(({ icon: AdvantageIcon, label }, index) => (
        <Fragment key={label}>
          {index > 0 && <span className={dividerClassName} />}
          <div className={itemClassName}>
            <AdvantageIcon className={iconClassName} />
            <p className={textClassName}>{label}</p>
          </div>
        </Fragment>
      ))}
    </div>
    {/* Transparent border matches the advantages row's real 2px one, so both
        rows keep the same content offset. */}
    <div className={classes([rowClassName, 'border-2 border-transparent'])}>
      {STATS.map(({ icon: StatIcon, value, label, shortLabel }, index) => (
        <Fragment key={label}>
          {index > 0 && <span className={dividerClassName} />}
          <div className={itemClassName}>
            <StatIcon className={iconClassName} />
            <p className={classes([textClassName, 'font-semibold'])}>{value}</p>
            <p className={textClassName}>
              <span className="min-[1025px]:hidden">{shortLabel}</span>
              <span className="hidden min-[1025px]:inline">{label}</span>
            </p>
          </div>
        </Fragment>
      ))}
    </div>
  </div>
);

export default TrustPanel;
